import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { useIsMobile, hScroll } from '../utils/useMobile';

// ═══ 말소대행 작업 유형(단가 마스터) 탭 ═══
// 배경 — 정산행의 작업 유형은 지금까지 note 자유입력으로만 관리됐다(운영 대행사 3 기준 9,499행:
// 등재 421 / 임판 번호판 120 / 등재만 43 / 이전 34 …). 유형마다 금액 구성이 다른데(일반 말소는
// 등록세 15,000·증지 2,300·수수료 5,700~6,200, 임판 번호판은 등록세 0·증지 1,800·수수료 20,200)
// 마스터가 없어 매번 손으로 쳤다. 이 화면이 그 단가의 유일한 쓰기 화면이고,
// 정산 탭의 수기 항목 추가/편집 모달이 이걸 프리셋으로 읽어 폼을 채운다.
//
// 저장 포맷은 무변경 — 유형은 입력 보조일 뿐이고 cancel_settlement_items 에는 확정 금액만 남는다.
// (정산행에 workTypeId 를 저장하지 않는다.)
//
// null vs 0 — serviceFee / registrationTax / stampDuty 는 nullable.
//   null = "기본값 사용"(전역 기본 또는 거래처별 요율), 0 = "0원 청구".
//   폼은 이 둘을 문자열 ''(빈칸) 과 '0' 으로 구분해 들고, 전송 시 numOrNull 로 각각 null / 0 을 보낸다.
//   목록은 null 을 '기본' 으로, 0 을 '0' 으로 그린다.
//
// 서버(GET /agency-work-types)가 아직 없거나 실패하면 빈 목록 + 안내로 degrade (화면은 죽지 않는다).
function CancelAgencyWorkTypes({ showToast, agencies, isCancelAgency }) {
  var isMobile = useIsMobile();   // ≤768px — 데스크탑 렌더는 이 분기 밖에서 불변
  var fmt = function(n){ return (parseInt(n,10)||0).toLocaleString(); };

  var [agencyId, setAgencyId] = useState('');   // admin/doc_agency 선택값 (cancel_agency 는 backend override)
  var [types, setTypes] = useState([]);
  var [unavailable, setUnavailable] = useState(false); // GET 실패/404 — 서버 미배포 폴백
  var [editing, setEditing] = useState(0);      // 편집 중 유형 id
  var [form, setForm] = useState(null);
  var [add, setAdd] = useState(null);           // ＋ 유형 추가 모달 폼
  var [del, setDel] = useState(null);           // 삭제 확인 모달 대상
  // 도움말 — 데스크탑은 펼친 채 시작(여백이 있다), 모바일은 접어 시작(표까지 스크롤 부담).
  // 접혀 있어도 요약 줄이 이 화면의 유일한 계약(빈칸 ≠ 0)을 계속 들고 있다.
  var [guideOpen, setGuideOpen] = useState(function(){ return !isMobile; });

  var canQuery = function(){ return isCancelAgency || !!agencyId; };
  var qs = function(){ return (!isCancelAgency && agencyId) ? ('&agencyId=' + agencyId) : ''; };
  var scopeBody = function(o){ if(!isCancelAgency && agencyId) o.agencyId = parseInt(agencyId,10); return o; };

  var load = function(){
    if(!canQuery()){ setTypes([]); setUnavailable(false); return; }
    authFetch(API+'/agency-work-types?includeInactive=1'+qs())
      .then(function(r){ if(!r.ok) throw new Error('HTTP '+r.status); return r.json(); })
      .then(function(d){ setTypes(Array.isArray(d)?d:[]); setUnavailable(false); })
      .catch(function(e){ console.error('[work-types] 목록 조회 실패', e); setTypes([]); setUnavailable(true); });
  };
  useEffect(function(){ load(); }, [agencyId, isCancelAgency]); // eslint-disable-line react-hooks/exhaustive-deps

  var closeAll = function(){ setEditing(0); setForm(null); };
  var setF = function(k,v){ setForm(function(p){ if(!p) return p; var n={}; for(var x in p)n[x]=p[x]; n[k]=v; return n; }); };
  var setA = function(k,v){ setAdd(function(p){ if(!p) return p; var n={}; for(var x in p)n[x]=p[x]; n[k]=v; return n; }); };

  // ── null vs 0 변환 (이 화면의 핵심 계약) ────────────────────────────────
  // 빈칸 → null ("기본값 사용"), '0' → 0 ("0원 청구"). 빈칸을 0 으로 접으면 안 된다.
  var numOrNull = function(s){ return String(s==null?'':s).trim()==='' ? null : (parseInt(s,10)||0); };
  var toInput = function(v){ return (v===null||v===undefined) ? '' : String(v); };
  var amtCell = function(v){
    if(v===null||v===undefined) return <span style={{color:'#94a3b8'}}>기본</span>;
    return fmt(v);
  };

  // ── 스타일 (거래처 탭과 동일 계열 — navy #1B3A5C / accent #2563EB / bluebg #eff6ff) ──
  var TH = {fontSize:isMobile?12:11,color:'#94a3b8',textAlign:'left',fontWeight:600,padding:isMobile?'9px 10px':'8px 12px',borderBottom:'1px solid #e2e8f0',background:'#f1f5f9',whiteSpace:isMobile?'nowrap':undefined};
  var TD = {padding:isMobile?'13px 10px':'9px 12px',borderBottom:'1px solid #f1f5f9',verticalAlign:'middle'};
  var HINT = {color:'#94a3b8'};
  // 모바일 = 2열 축약 (유형명 / 관리). 7열(minWidth 680)이면 390 화면에 수수료·등록세까지만 들어오고
  // 증지·정렬·활성·관리가 가로 스크롤 밖으로 나갔다(실측). 금액 3종은 이름 아래 메타 줄로 내린다.
  var COLS = isMobile ? 2 : 7;
  var metaSt = {display:'block',fontSize:12,color:'#94a3b8',marginTop:3,lineHeight:1.45};
  var metaDot = <span style={{color:'#cbd5e1'}}> · </span>;
  var badge = function(bg, color){
    return {display:'inline-block',fontSize:isMobile?12:10.5,fontWeight:700,borderRadius:5,padding:'1px 7px',marginLeft:6,background:bg,color:color,verticalAlign:1};
  };
  var inSt = {border:'1px solid #e2e8f0',borderRadius:6,padding:isMobile?'10px 12px':'6px 9px',fontSize:isMobile?13:12.5,background:'#fff',color:'#1e293b',width:'100%',boxSizing:'border-box',minHeight:isMobile?40:undefined};
  var lbSt = {fontSize:isMobile?12:11,fontWeight:700,color:'#64748b'};
  var capSt = {fontSize:isMobile?12:10.5,color:'#94a3b8'};
  var chkSt = isMobile
    ? {display:'flex',alignItems:'center',gap:8,fontSize:13,minHeight:40,whiteSpace:'nowrap'}
    : {display:'flex',alignItems:'center',gap:6,fontSize:12.5,paddingTop:16,whiteSpace:'nowrap'};
  var frmSt = isMobile ? {display:'flex',flexDirection:'column',gap:10} : {display:'flex',gap:10,flexWrap:'wrap'};
  var btnSt = isMobile ? {minHeight:40,padding:'0 14px',fontSize:13} : undefined;
  var modalWrap = {position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16};
  var modalBox = {background:'#fff',borderRadius:14,padding:isMobile?16:20,width:520,maxWidth:isMobile?'94vw':'100%',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 12px 40px rgba(0,0,0,0.18)'};

  var fld = function(label, node, width, caption){
    var box = isMobile
      ? {width:'100%'}
      : {width:width||undefined, flex:width?undefined:1, minWidth:width?undefined:140};
    return (<div style={Object.assign({display:'flex',flexDirection:'column',gap:3}, box)}>
      <label style={lbSt}>{label}</label>
      {node}
      {caption ? <small style={capSt}>{caption}</small> : null}
    </div>);
  };
  var textIn = function(val, on, ph, right){
    return <input value={val} onChange={function(e){on(e.target.value)}} placeholder={ph||''} style={Object.assign({},inSt,right?{textAlign:'right'}:null)} />;
  };

  // ── 변이 공통 (거래처 탭 send 규약과 동일) ──────────────────────────────
  var send = function(url, method, body, okMsg, after){
    var opt = { method: method };
    if(body){ opt.headers = {'Content-Type':'application/json'}; opt.body = JSON.stringify(body); }
    authFetch(url, opt).then(function(r){ return r.json().catch(function(){ return {}; }); }).then(function(d){
      if(d && d.success){ showToast(okMsg); closeAll(); load(); if(after) after(d); return; }
      showToast((d && d.error) || '처리 실패', 'err');
    }).catch(function(){ showToast('처리 실패','err'); });
  };

  var startEdit = function(r){
    setEditing(r.id);
    setForm({ name:r.name||'', serviceFee:toInput(r.serviceFee), registrationTax:toInput(r.registrationTax),
      stampDuty:toInput(r.stampDuty), sortOrder:String(r.sortOrder||0), active:(r.active?1:0) });
  };
  var save = function(r){
    if(!form.name || !form.name.trim()){ showToast('유형명을 입력하세요','err'); return; }
    // 세 금액은 항상 전송한다 — 빈칸으로 지운 것(null 복귀)도 반영돼야 하므로 '미전송 = 보존' 에 기대지 않는다.
    send(API+'/agency-work-types/'+r.id, 'PUT', scopeBody({
      name: form.name.trim(),
      serviceFee: numOrNull(form.serviceFee),
      registrationTax: numOrNull(form.registrationTax),
      stampDuty: numOrNull(form.stampDuty),
      sortOrder: parseInt(form.sortOrder,10)||0,
      active: form.active?1:0
    }), '수정 완료');
  };
  var setActive = function(r, active){
    send(API+'/agency-work-types/'+r.id, 'PUT', scopeBody({active: active?1:0}), active?'다시 활성화했습니다':'비활성으로 전환했습니다');
  };
  var doDelete = function(){
    send(API+'/agency-work-types/'+del.id, 'DELETE', null, '삭제 완료', function(){ setDel(null); });
  };

  var startAdd = function(){
    closeAll();
    setAdd({ name:'', serviceFee:'', registrationTax:'', stampDuty:'', sortOrder:'' });
  };
  var submitAdd = function(){
    if(!add.name || !add.name.trim()){ showToast('유형명을 입력하세요','err'); return; }
    send(API+'/agency-work-types', 'POST', scopeBody({
      name: add.name.trim(),
      serviceFee: numOrNull(add.serviceFee),
      registrationTax: numOrNull(add.registrationTax),
      stampDuty: numOrNull(add.stampDuty),
      sortOrder: parseInt(add.sortOrder,10)||0,
      active: 1
    }), '작업 유형 추가 완료', function(){ setAdd(null); });
  };

  // ── 편집 폼 (데스크탑 = 인라인 행 / 모바일 = 하단 시트, 같은 노드) ──────
  var editFields = function(r){
    return (<React.Fragment>
      <div style={frmSt}>
        {fld('유형명', textIn(form.name, function(v){setF('name',v)}, '예: 임판 번호판'))}
        {fld('정렬', textIn(form.sortOrder, function(v){setF('sortOrder',v)}, '0', true), 90, '작은 값이 위')}
        <label style={chkSt}><input type="checkbox" checked={!!form.active} onChange={function(e){setF('active',e.target.checked?1:0)}} /> 활성</label>
      </div>
      <div style={frmSt}>
        {fld('수수료', textIn(form.serviceFee, function(v){setF('serviceFee',v)}, '비우면 기본', true), 130, '빈칸 = 거래처 요율')}
        {fld('등록세', textIn(form.registrationTax, function(v){setF('registrationTax',v)}, '비우면 기본', true), 130, '빈칸 = 15,000 / 0 = 0원')}
        {fld('증지', textIn(form.stampDuty, function(v){setF('stampDuty',v)}, '비우면 기본', true), 130, '빈칸 = 2,300 / 0 = 0원')}
      </div>
      <div style={Object.assign({display:'flex',gap:8,alignItems:'center'}, isMobile?{flexWrap:'wrap'}:null)}>
        <button className="btn btn-blue btn-sm" style={btnSt} onClick={function(){save(r)}}>저장</button>
        <button className="btn btn-outline btn-sm" style={btnSt} onClick={closeAll}>취소</button>
        <div style={{flex:1}} />
        <button className="btn btn-outline btn-sm" style={Object.assign({color:'#dc2626'}, btnSt)}
          onClick={function(){ setDel({id:r.id, name:r.name}); }}>삭제</button>
      </div>
    </React.Fragment>);
  };

  var editRow = function(r){
    return (<tr key={'e'+r.id}>
      <td colSpan={COLS} style={{background:'#fff',padding:0,borderBottom:'1px solid #e2e8f0'}}>
        <div style={{borderLeft:'3px solid #2563EB',padding:'14px 18px',display:'flex',flexDirection:'column',gap:12}}>
          {editFields(r)}
        </div>
      </td>
    </tr>);
  };

  // 모바일 편집 시트 — 표(가로 스크롤) 밖 fixed 오버레이 (거래처 탭 editSheet 규약과 동일)
  var editSheet = function(title, body){
    return (<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.45)',zIndex:9998,display:'flex',flexDirection:'column',justifyContent:'flex-end'}}
      onClick={closeAll}>
      <div onClick={function(e){e.stopPropagation()}}
        style={{background:'#fff',borderTopLeftRadius:16,borderTopRightRadius:16,maxHeight:'92vh',overflowY:'auto',WebkitOverflowScrolling:'touch',padding:'14px 14px 28px',boxShadow:'0 -8px 32px rgba(0,0,0,0.2)'}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:8,marginBottom:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:800,color:'#1B3A5C',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{title}</div>
            <div style={{fontSize:12,color:'#94a3b8',marginTop:2}}>비우면 기본값 사용 · 0 은 0원 청구</div>
          </div>
          <button onClick={closeAll} style={{border:'none',background:'none',fontSize:18,color:'#94a3b8',minWidth:40,minHeight:40,cursor:'pointer',padding:0}}>✕</button>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>{body}</div>
      </div>
    </div>);
  };

  var row = function(r){
    var editingThis = editing === r.id;
    var tdSt = Object.assign({}, TD, {background: r.active?'#fff':'#f8fafc', opacity: r.active?1:0.7},
      isMobile?{wordBreak:'keep-all', overflowWrap:'anywhere'}:null);
    var numSt = Object.assign({}, tdSt, {textAlign:'right'});
    return (<React.Fragment key={'t'+r.id}>
      {/* 행 클릭 = 편집 토글 (거래처 탭과 동일). 버튼 클릭은 편집 진입 제외 */}
      <tr style={{cursor:'pointer'}} title={editingThis?undefined:'클릭해 수정'}
        onClick={function(e){ if(e.target.closest && e.target.closest('button')) return; editingThis?closeAll():startEdit(r); }}>
        <td style={tdSt}>
          <span style={{color: r.active?'#1e293b':'#64748b',fontWeight:600}}>{r.name}</span>
          {!r.active && <span style={badge('#e2e8f0','#64748b')}>비활성</span>}
          {isMobile && <span style={metaSt}>
            수수료 {amtCell(r.serviceFee)}{metaDot}등록세 {amtCell(r.registrationTax)}{metaDot}증지 {amtCell(r.stampDuty)}
          </span>}
        </td>
        {!isMobile && <td style={numSt}>{amtCell(r.serviceFee)}</td>}
        {!isMobile && <td style={numSt}>{amtCell(r.registrationTax)}</td>}
        {!isMobile && <td style={numSt}>{amtCell(r.stampDuty)}</td>}
        {!isMobile && <td style={Object.assign({},numSt,HINT)}>{r.sortOrder||0}</td>}
        {!isMobile && <td style={tdSt}>{r.active ? <span style={{color:'#059669',fontWeight:700}}>활성</span> : <span style={HINT}>비활성</span>}</td>}
        <td style={Object.assign({},tdSt,{textAlign:'right',whiteSpace:'nowrap'})}>
          <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setActive(r,!r.active)}}>{r.active?'비활성':'다시 활성'}</button>
        </td>
      </tr>
      {editingThis && form && !isMobile && editRow(r)}
    </React.Fragment>);
  };

  var editRec = (editing && form) ? (types.filter(function(t){return t.id===editing})[0] || null) : null;

  // ── 도움말 (문단 나열 → 흐름 3단계 + 예시 표 + 기본값 칩) ────────────────
  // 텍스트를 줄이는 게 목적이 아니라 "어느 값이 이기는가" 를 눈으로 읽히게 하는 게 목적.
  // 표시 안내 시트(CancelAgency.js)와 같은 어휘 — 실물과 같은 '기본' 회색 칩을 그대로 그린다.
  var gTitle = {fontSize:12.5,fontWeight:800,color:'#1B3A5C'};
  var gDesc = {fontSize:isMobile?12:11.5,color:'#64748b',lineHeight:1.55};
  var stepCard = function(n, title, desc){
    return (<div style={{flex:1,minWidth:0,display:'flex',gap:9,alignItems:'flex-start',
      border:'1px solid #e2e8f0',borderRadius:8,background:'#f8fafc',padding:'10px 12px'}}>
      <span style={{flexShrink:0,width:18,height:18,borderRadius:9,background:'#1B3A5C',color:'#fff',
        fontSize:11,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',marginTop:1}}>{n}</span>
      <span style={{minWidth:0}}>
        <span style={Object.assign({display:'block',marginBottom:2},gTitle)}>{title}</span>
        <span style={Object.assign({display:'block'},gDesc)}>{desc}</span>
      </span>
    </div>);
  };
  var arrow = function(k){
    return <span key={k} style={{flexShrink:0,color:'#cbd5e1',fontSize:13,fontWeight:800,
      alignSelf:isMobile?'flex-start':'center',padding:isMobile?'1px 0 1px 9px':'0 2px'}}>{isMobile?'↓':'→'}</span>;
  };
  var chip = function(t){
    return <span key={t} style={{display:'inline-block',border:'1px solid #bfdbfe',background:'#eff6ff',color:'#1d4ed8',
      borderRadius:999,padding:'3px 9px',fontSize:isMobile?12:11.5,fontWeight:700,whiteSpace:'nowrap'}}>{t}</span>;
  };
  var eTH = {fontSize:11,color:'#94a3b8',fontWeight:700,textAlign:'left',padding:'6px 8px',background:'#f1f5f9',borderBottom:'1px solid #e2e8f0'};
  var eTD = {fontSize:isMobile?12:12.5,color:'#334155',padding:isMobile?'8px 8px':'7px 8px',borderBottom:'1px solid #f1f5f9',verticalAlign:'top'};
  var exRow = function(k, given, amount, meaning, warn){
    return (<tr key={k}>
      <td style={Object.assign({},eTD,{textAlign:'right',whiteSpace:'nowrap'})}>{given}</td>
      <td style={Object.assign({},eTD,{textAlign:'right',whiteSpace:'nowrap',fontWeight:700})}>{amount}</td>
      <td style={Object.assign({},eTD,warn?{color:'#b45309',fontWeight:700}:null)}>{meaning}</td>
    </tr>);
  };
  var guideBody = (
    <div style={{border:'1px solid #e2e8f0',borderRadius:10,background:'#fff',padding:isMobile?'12px 12px 14px':'14px 16px 16px',marginTop:8}}>
      <div style={Object.assign({marginBottom:8},gTitle)}>단가가 정해지는 순서</div>
      <div style={{display:'flex',flexDirection:isMobile?'column':'row',gap:isMobile?0:8,alignItems:'stretch'}}>
        {stepCard(1,'이 화면 — 작업 유형','대행사가 공통으로 쓰는 기본 단가를 유형별로 정해 둡니다.')}
        {arrow('a1')}
        {stepCard(2,'거래처 — 유형별 단가','금액이 다른 거래처만 그 거래처 폼에서 다른 값으로 덮어씁니다.')}
        {arrow('a2')}
        {stepCard(3,'정산 반영','말소 완료·말소증 등록 시 거래처 값을 먼저 쓰고, 없으면 이 화면 값으로 채웁니다.')}
      </div>

      <div style={Object.assign({margin:'14px 0 2px'},gTitle)}>거래처에 넣은 값에 따라</div>
      <div style={Object.assign({marginBottom:6},gDesc)}>수수료 기본 단가가 20,200 인 유형을 예로 들면</div>
      <div style={{border:'1px solid #e2e8f0',borderRadius:8,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
          <colgroup><col style={{width:isMobile?'27%':'20%'}} /><col style={{width:isMobile?'27%':'20%'}} /><col /></colgroup>
          <thead><tr>
            <th style={Object.assign({},eTH,{textAlign:'right'})}>거래처 값</th>
            <th style={Object.assign({},eTH,{textAlign:'right'})}>정산 금액</th>
            <th style={eTH}>뜻</th>
          </tr></thead>
          <tbody>
            {exRow('r1','25,000','25,000','거래처에 넣은 값이 그대로 청구됩니다.')}
            {exRow('r2', amtCell(null), '20,200', '비우면 이 화면의 기본 단가로 채워집니다.')}
            {exRow('r3','0','0','"0원 청구" 로 확정됩니다 — 빈칸과 다릅니다.', true)}
          </tbody>
        </table>
      </div>
      <div style={Object.assign({marginTop:6},gDesc)}>예) 임판 번호판 — 등록세 <b>0</b> · 증지 1,800 · 수수료 20,200</div>

      <div style={Object.assign({margin:'14px 0 2px'},gTitle)}>비웠을 때 쓰이는 기본값</div>
      <div style={Object.assign({marginBottom:6},gDesc)}>아래 목록에서 금액 대신 {amtCell(null)} 이라고 보이는 칸이 여기에 해당합니다</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
        {['등록세 15,000','증지 2,300','수수료 = 거래처 요율'].map(chip)}
      </div>
      <div style={Object.assign({marginTop:10,paddingTop:10,borderTop:'1px solid #f1f5f9'},gDesc)}>
        여기 값은 <b>정산 탭의 수기 항목 추가/편집</b> 에서 프리셋으로 불러 쓰는 입력 보조입니다 — 정산행에는 확정 금액만 저장됩니다.
      </div>
    </div>
  );
  var guide = (
    <div style={{marginBottom:12}}>
      <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:isMobile?'8px 10px':'8px 12px',
        display:'flex',alignItems:'center',gap:8,flexWrap:'nowrap'}}>
        <span style={{flex:1,minWidth:0,fontSize:isMobile?12.5:13,color:'#92400e',lineHeight:1.5}}>
          금액을 <b>비우면</b> 기본값 · <b>0</b> 을 넣으면 0원 청구 — 빈칸과 0 은 다릅니다.
        </span>
        <button type="button" aria-expanded={guideOpen ? 'true' : 'false'} onClick={function(){setGuideOpen(!guideOpen)}}
          style={{flexShrink:0,border:'1px solid #fcd34d',background:'#fff',color:'#92400e',borderRadius:6,cursor:'pointer',
            fontWeight:700,fontSize:12,padding:isMobile?'0 10px':'5px 10px',minHeight:isMobile?40:undefined,whiteSpace:'nowrap'}}>
          {guideOpen ? '도움말 ▴' : '도움말 ▾'}
        </button>
      </div>
      {guideOpen && guideBody}
    </div>
  );

  return (
    <div>
      {/* 상단: 대행사 선택(admin) + 추가 + 새로고침 */}
      <div style={{display:'flex',alignItems:'center',gap:isMobile?8:10,marginBottom:12,flexWrap:'wrap'}}>
        {!isCancelAgency && (
          <select value={agencyId} onChange={function(e){setAgencyId(e.target.value);closeAll();}}
            style={Object.assign({padding:isMobile?'10px 12px':'8px 10px',border:'1px solid #cbd5e1',borderRadius:8}, isMobile?{flex:'1 1 100%',width:'100%',minWidth:0,boxSizing:'border-box',fontSize:13,minHeight:40}:null)}>
            <option value="">대행사 선택…</option>
            {(agencies||[]).filter(function(a){return a.active}).map(function(a){return <option key={a.id} value={a.id}>{a.name}</option>})}
          </select>
        )}
        {canQuery() && <span style={Object.assign({fontSize:12,color:'#94a3b8'}, isMobile?{flex:'1 1 100%',lineHeight:1.6}:null)}>
          작업 유형 <b style={{color:'#475569'}}>{types.length}</b>개
        </span>}
        {!isMobile && <div style={{flex:1}} />}
        {canQuery() && (isMobile
          ? (<div style={{display:'flex',gap:8,width:'100%',justifyContent:'flex-end'}}>
              <button className="btn btn-blue btn-sm" style={btnSt} onClick={startAdd}>＋ 유형 추가</button>
              <button className="btn btn-outline btn-sm" style={btnSt} onClick={load}>새로고침</button>
            </div>)
          : (<React.Fragment>
              <button className="btn btn-blue btn-sm" onClick={startAdd}>＋ 유형 추가</button>
              <button className="btn btn-outline btn-sm" onClick={load}>새로고침</button>
            </React.Fragment>))}
      </div>

      {canQuery() && guide}

      {!canQuery() && <div style={{color:'#94a3b8',padding:'40px 0',textAlign:'center'}}>대행사를 선택하세요.</div>}

      {canQuery() && unavailable && (
        <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:13,color:'#991b1b'}}>
          작업 유형 목록을 불러오지 못했습니다 — 서버가 아직 이 기능을 제공하지 않을 수 있습니다.
        </div>
      )}

      {canQuery() && (
        <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,overflow:'hidden'}}>
          <div style={isMobile?hScroll:undefined}>
          {/* 모바일 minWidth 는 2열이 다 들어가는 최소치 (320px 기기 대비 래퍼는 그대로) */}
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13,minWidth:isMobile?300:undefined}}>
            {isMobile
              ? (<colgroup><col style={{width:'68%'}} /><col style={{width:'32%'}} /></colgroup>)
              : (<colgroup>
                  <col style={{width:'28%'}} /><col style={{width:'13%'}} /><col style={{width:'13%'}} />
                  <col style={{width:'13%'}} /><col style={{width:'8%'}} /><col style={{width:'10%'}} /><col style={{width:'15%'}} />
                </colgroup>)}
            <thead>
              <tr>
                <th style={TH}>유형명</th>
                {!isMobile && <th style={Object.assign({},TH,{textAlign:'right'})}>수수료</th>}
                {!isMobile && <th style={Object.assign({},TH,{textAlign:'right'})}>등록세</th>}
                {!isMobile && <th style={Object.assign({},TH,{textAlign:'right'})}>증지</th>}
                {!isMobile && <th style={Object.assign({},TH,{textAlign:'right'})}>정렬</th>}
                {!isMobile && <th style={TH}>활성</th>}
                <th style={Object.assign({},TH,{textAlign:'right'})}>관리</th>
              </tr>
            </thead>
            <tbody>
              {types.map(function(r){ return row(r); })}
              {types.length === 0 && <tr><td colSpan={COLS} style={{textAlign:'center',color:'#94a3b8',padding:'30px 0'}}>등록된 작업 유형이 없습니다.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* 모바일 편집 시트 — 인라인 편집 행 대체 (표 가로 스크롤 밖) */}
      {isMobile && form && editRec && editSheet(editRec.name || '작업 유형 수정', editFields(editRec))}

      {/* 하단 스페이서 — 최하단 행이 화면 끝에 붙지 않게 */}
      {canQuery() && <div style={{height:200}} />}

      {/* ＋ 유형 추가 */}
      {add && (<div style={modalWrap} onClick={function(){setAdd(null)}}>
        <div style={modalBox} onClick={function(e){e.stopPropagation()}}>
          <h3 style={{margin:'0 0 4px',fontSize:15,color:'#1B3A5C'}}>작업 유형 추가</h3>
          <p style={{fontSize:12,color:'#64748b',margin:'0 0 14px'}}>금액은 비워두면 기본값이 적용됩니다 — 0 을 넣으면 "0원 청구" 입니다.</p>
          <div style={frmSt}>
            {fld('유형명', textIn(add.name, function(v){setA('name',v)}, '예: 임판 번호판'))}
            {fld('정렬', textIn(add.sortOrder, function(v){setA('sortOrder',v)}, '0', true), 90, '작은 값이 위')}
          </div>
          <div style={Object.assign({},frmSt,{marginTop:10})}>
            {fld('수수료', textIn(add.serviceFee, function(v){setA('serviceFee',v)}, '비우면 기본', true), 130, '빈칸 = 거래처 요율')}
            {fld('등록세', textIn(add.registrationTax, function(v){setA('registrationTax',v)}, '비우면 기본', true), 130, '빈칸 = 15,000 / 0 = 0원')}
            {fld('증지', textIn(add.stampDuty, function(v){setA('stampDuty',v)}, '비우면 기본', true), 130, '빈칸 = 2,300 / 0 = 0원')}
          </div>
          <div style={Object.assign({display:'flex',gap:8,alignItems:'center',marginTop:14}, isMobile?{flexWrap:'wrap'}:null)}>
            <div style={{flex:1}} />
            <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setAdd(null)}}>취소</button>
            <button className="btn btn-blue btn-sm" style={btnSt} onClick={submitAdd}>추가</button>
          </div>
        </div>
      </div>)}

      {/* 삭제 확인 — 브라우저 confirm 대신 이 레포의 확인 모달 패턴 */}
      {del && (<div style={modalWrap} onClick={function(){setDel(null)}}>
        <div style={Object.assign({},modalBox,{width:440})} onClick={function(e){e.stopPropagation()}}>
          <h3 style={{margin:'0 0 4px',fontSize:15,color:'#1B3A5C'}}>작업 유형 "{del.name}" 을(를) 삭제할까요?</h3>
          <p style={{fontSize:12,color:'#64748b',margin:'0 0 14px'}}>
            이미 기록된 정산 항목의 금액은 그대로 남습니다 (유형은 입력 프리셋일 뿐입니다).
            잠시 쓰지 않는 것뿐이라면 <b>비활성</b> 으로 두는 편이 안전합니다.
          </p>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <div style={{flex:1}} />
            <button className="btn btn-outline btn-sm" style={btnSt} onClick={function(){setDel(null)}}>취소</button>
            <button className="btn btn-outline btn-sm" style={Object.assign({color:'#dc2626'}, btnSt)} onClick={doDelete}>삭제</button>
          </div>
        </div>
      </div>)}
    </div>
  );
}

export default CancelAgencyWorkTypes;
